import { Node } from '@squaredup/graph';
import { type Virtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { useEffect } from 'react';

interface ObjectTableBodyProps {
    objects: Node[];
    rowVirtualizer: Virtualizer<any, Element>;
    resetScrollKey: string;
    hasNextObjectsPage?: boolean;
    isFetchingNextObjectsPage: boolean;
    isFetchingObjects: boolean;
    fetchNextObjectsPage: () => void;
    renderObjectRow: (object: Node, props: Record<string, unknown>, index: number) => React.ReactNode;
}

export const ObjectTableBody: React.FC<ObjectTableBodyProps> = ({
    objects,
    rowVirtualizer,
    resetScrollKey,
    hasNextObjectsPage,
    isFetchingNextObjectsPage,
    isFetchingObjects,
    fetchNextObjectsPage,
    renderObjectRow
}) => {
    useEffect(() => {
        // When a filter is changed, scroll the user to the first item in the list
        if (rowVirtualizer.getVirtualItems().length > 0) {
            rowVirtualizer.scrollToIndex(0);
        }
    }, [resetScrollKey]);

    useEffect(() => {
        const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

        if (!lastItem || !objects) {
            return;
        }

        const nextPageBuffer = 6;
        if (lastItem.index >= objects.length - nextPageBuffer && hasNextObjectsPage && !isFetchingNextObjectsPage) {
            fetchNextObjectsPage();
        }
    }, [
        hasNextObjectsPage,
        fetchNextObjectsPage,
        objects?.length,
        isFetchingNextObjectsPage,
        rowVirtualizer.getVirtualItems()
    ]);

    return (
        <tbody className={clsx('min-h-0', isFetchingObjects && 'pointer-events-none')}>
            {rowVirtualizer.getVirtualItems().map((virtualItem, i) => {
                const object = objects[virtualItem.index];

                if (object == null) {
                    return <></>;
                }

                return renderObjectRow(
                    object,
                    {
                        key: virtualItem.key.toString(),
                        style: {
                            height: `${virtualItem.size}px !important`,
                            transform: `translateY(${virtualItem.start - i * virtualItem.size}px)`
                        }
                    },
                    virtualItem.index
                );
            })}

            {/* Without this row taking all remaining height, the fixed height of the tbody
                makes it stretch the rows above vertically, ignoring their fixed heights. */}
            <tr className='h-full'></tr>
        </tbody>
    );
};
